import { Injectable, ErrorHandler, Injector } from '@angular/core';
import { ErrorHandlerService } from './services/error-handler.service';
import { CrmGraphql } from './shared/constants/graphql-constants';
import { Apollo } from 'apollo-angular';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { CRM_B2C_FORGOT_PASSWORD_RECOVERY_SESSION_KEY } from 'src/app/app-config';
import gql from 'graphql-tag';

const enum ERROR_CATEGORIES {
  NETWORK = 100,
  VALIDATION = 200,
  AUTHENTICATION = 300,
  AUTHORIZATION = 400,
  DATA_ACCESS = 500,
  SERVER = 600,
  CLIENT = 700,
  TIMEOUT = 800,
  UNKNOWN = 900
}

const enum MODULE_CODES {
  LOGIN = 'LG',
  CONTRACTS = 'CT',
  SERVICES = 'SV',
  DEALERS = 'DL',
  BUYING_GROUP = 'BG',
  CLAIMS = 'CL',
  CATALOG = 'CA',
  COVERAGE = 'CV',
  ADMINISTRATION = 'AD',
  REPORTS = 'RP',
  IMPORT_FILE = 'IF',
  UNKNOWN = 'UK'
}

const enum GLOBAL_ERROR_CODES {
  ERROR_NETWORK = 'Unable to load the page. Please refresh your browser tab and try again.',
  ERROR_TIME_OUT = 'Request Timeout. Please Try Again',
  ERROR_API = 'Something went wrong. Please Try Again',
  ERROR_SP_AVAILABILITY = 'Something went wrong on service provider availability, Please Try Again',
  ERROR_UNAUTHORIZED = 'Your session has expired. Please log in again to continue.',
  ERROR_FORBIDDEN = 'You do not have permission to perform this action. Please contact your administrator.',
  ERROR_NOT_FOUND = 'The requested information could not be found. It may have been moved or deleted.',
  ERROR_CONFLICT = 'The information has been modified by another user. Please refresh the page and try again.',
  ERROR_VALIDATION = 'The submitted information is invalid. Please check your entries and try again.',
  ERROR_TOO_MANY_REQUESTS = 'Too many requests have been made. Please wait a moment and try again.',
  ERROR_PROPERTY_ACCESS = 'A data processing issue occurred. Please try again or refresh the page.',
  ERROR_DEFAULT = 'The application has encountered an error. Please try again or reach out to CSG Support.',
  // Authentication specific errors
  ERROR_TOKEN_EXPIRED = 'Your login session has expired. Please log in again to continue.',
  ERROR_TOKEN_INVALID = 'Invalid authentication token. Please log in again.',
  ERROR_LOGIN_REQUIRED = 'Authentication required. Please log in to access this feature.',
  ERROR_PASSWORD_RESET_REQUIRED = 'Password reset is required. Please check your email for reset instructions.',
  ERROR_MFA_REQUIRED = 'Multi-factor authentication is required. Please complete the verification process.',
  ERROR_INTERACTION_IN_PROGRESS = 'Authentication is already in progress. Please wait for it to complete.',
  ERROR_LOGIN_CANCELLED = 'Login was cancelled. Please try again to access the application.',
  ERROR_AZURE_B2C = 'Authentication service error. Please try again or contact support if the issue persists.'
}

interface ErrorPattern {
  name: string;
  code: GLOBAL_ERROR_CODES;
  category: ERROR_CATEGORIES;
}

const GLOBAL_ERROR_PATTERNS: ErrorPattern[] = [
  {
    name: 'Token renewal operation failed due to timeout',
    code: GLOBAL_ERROR_CODES.ERROR_TOKEN_EXPIRED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'User does not have an existing session',
    code: GLOBAL_ERROR_CODES.ERROR_LOGIN_REQUIRED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'AADB2C90077',
    code: GLOBAL_ERROR_CODES.ERROR_LOGIN_REQUIRED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'AADB2C90091',
    code: GLOBAL_ERROR_CODES.ERROR_LOGIN_CANCELLED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'AADB2C90118',
    code: GLOBAL_ERROR_CODES.ERROR_PASSWORD_RESET_REQUIRED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'AADB2C90205',
    code: GLOBAL_ERROR_CODES.ERROR_MFA_REQUIRED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'interaction_in_progress',
    code: GLOBAL_ERROR_CODES.ERROR_INTERACTION_IN_PROGRESS,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'invalid_token',
    code: GLOBAL_ERROR_CODES.ERROR_TOKEN_INVALID,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'token_expired',
    code: GLOBAL_ERROR_CODES.ERROR_TOKEN_EXPIRED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'login_required',
    code: GLOBAL_ERROR_CODES.ERROR_LOGIN_REQUIRED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'Cannot read properties of null',
    code: GLOBAL_ERROR_CODES.ERROR_PROPERTY_ACCESS,
    category: ERROR_CATEGORIES.DATA_ACCESS,
  },
  {
    name: 'Cannot read properties of undefined',
    code: GLOBAL_ERROR_CODES.ERROR_PROPERTY_ACCESS,
    category: ERROR_CATEGORIES.DATA_ACCESS,
  },
  {
    name: 'Cannot read property',
    code: GLOBAL_ERROR_CODES.ERROR_PROPERTY_ACCESS,
    category: ERROR_CATEGORIES.DATA_ACCESS,
  },
  {
    name: 'Network error',
    code: GLOBAL_ERROR_CODES.ERROR_NETWORK,
    category: ERROR_CATEGORIES.NETWORK,
  },
  {
    name: 'Http failure response',
    code: GLOBAL_ERROR_CODES.ERROR_NETWORK,
    category: ERROR_CATEGORIES.NETWORK,
  },
  {
    name: 'Failed to fetch',
    code: GLOBAL_ERROR_CODES.ERROR_NETWORK,
    category: ERROR_CATEGORIES.NETWORK,
  },
  {
    name: 'timeout',
    code: GLOBAL_ERROR_CODES.ERROR_TIME_OUT,
    category: ERROR_CATEGORIES.TIMEOUT,
  },
  {
    name: 'Timeout has occurred',
    code: GLOBAL_ERROR_CODES.ERROR_TIME_OUT,
    category: ERROR_CATEGORIES.TIMEOUT,
  },
  {
    name: 'serviceprovideravailability',
    code: GLOBAL_ERROR_CODES.ERROR_SP_AVAILABILITY,
    category: ERROR_CATEGORIES.SERVER,
  },
  {
    name: 'Unauthorized',
    code: GLOBAL_ERROR_CODES.ERROR_UNAUTHORIZED,
    category: ERROR_CATEGORIES.AUTHENTICATION,
  },
  {
    name: 'Forbidden',
    code: GLOBAL_ERROR_CODES.ERROR_FORBIDDEN,
    category: ERROR_CATEGORIES.AUTHORIZATION,
  },
  {
    name: 'Not found',
    code: GLOBAL_ERROR_CODES.ERROR_NOT_FOUND,
    category: ERROR_CATEGORIES.CLIENT,
  },
  {
    name: 'Conflict',
    code: GLOBAL_ERROR_CODES.ERROR_CONFLICT,
    category: ERROR_CATEGORIES.VALIDATION,
  },
  {
    name: 'Bad request',
    code: GLOBAL_ERROR_CODES.ERROR_VALIDATION,
    category: ERROR_CATEGORIES.VALIDATION,
  },
  {
    name: 'Too many requests',
    code: GLOBAL_ERROR_CODES.ERROR_TOO_MANY_REQUESTS,
    category: ERROR_CATEGORIES.CLIENT,
  }
];

@Injectable()
export class CrmErrorHandler implements ErrorHandler {
  private errorSequenceCounters = new Map<string, number>();

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private apollo: Apollo,
    private injector: Injector
  ) {}

  handleError(error: Error) {
    const ignoreList = [
      'ExpressionChangedAfterItHasBeenCheckedError',
      `"There is no FormControl instance attached to form control element with name: 'customText'"`,
      `Failed to set an indexed property on `,
      `R3InjectorError(AppModule)[NgControl -> NgControl -> NgControl]:`,
      `CSSStyleDeclaration`
    ];
    console.log('Error', error);
    let ignoreFlag = true;
    for (const ignoreStr of ignoreList) {
      if (error?.message?.includes(ignoreStr)) {
        ignoreFlag = true;
        break;
      }
    }

    if (!ignoreFlag) {
      const errorResult = this.processErrorMessage(error);
      const shouldRedirectSilently = this.handleAuthenticationErrors(error, errorResult);
      
      // Only show error notification if we're not redirecting silently
      if (!shouldRedirectSilently) {
        this.errorHandlerService.errorNotification.next(errorResult.message);
        this.logErrorToBackend(error, errorResult);
      }
    }
  }

  private processErrorMessage(error: any): { message: string; errorCode: string } {
    if (!error) {
      return {
        message: GLOBAL_ERROR_CODES.ERROR_DEFAULT,
        errorCode: this.generateErrorCode(ERROR_CATEGORIES.UNKNOWN, MODULE_CODES.UNKNOWN)
      };
    }

    let errorMessage = GLOBAL_ERROR_CODES.ERROR_DEFAULT;
    let errorCategory = ERROR_CATEGORIES.UNKNOWN;
    let errorString = '';

    if (typeof error === 'string') {
      errorString = error;
    } else if (typeof error === 'object') {
      errorString = this.extractErrorString(error);
    } else {
      errorString = String(error);
    }

    console.log('Processing error - Original error:', error);
    console.log('Processing error - Extracted string:', errorString);

    const moduleCode = this.detectModule(error, errorString);
    console.log('Detected module:', moduleCode);
    
    // Check HTTP status first (handles Apollo errors, standard HTTP errors, etc.)
    const httpStatusResult = this.getHttpStatusMessage(error);
    if (httpStatusResult) {
      console.log('Using HTTP status message:', httpStatusResult.message);
      const errorCode = this.generateErrorCode(httpStatusResult.category, moduleCode);
      return {
        message: `${httpStatusResult.message} [Error Code: ${errorCode}]`,
        errorCode
      };
    }

    // Before pattern matching, check if errorString contains a status code
    // If it does, prioritize HTTP status-based categorization
    const statusInString = errorString.match(/: (\d{3})/);
    if (statusInString) {
      const extractedStatus = parseInt(statusInString[1], 10);
      const statusBasedResult = this.getHttpStatusMessage({ status: extractedStatus });
      if (statusBasedResult) {
        console.log('Using HTTP status message (from error string):', statusBasedResult.message);
        const errorCode = this.generateErrorCode(statusBasedResult.category, moduleCode);
        return {
          message: `${statusBasedResult.message} [Error Code: ${errorCode}]`,
          errorCode
        };
      }
    }

    for (const pattern of GLOBAL_ERROR_PATTERNS) {
      if (errorString.includes(pattern.name)) {
        // Skip "Http failure response" pattern if we detected a 5xx status code
        // (those should be server errors, not network errors)
        if (pattern.name === 'Http failure response' && statusInString) {
          const status = parseInt(statusInString[1], 10);
          if (status >= 500 && status < 600) {
            console.log('Skipping network error pattern for 5xx status code');
            continue; // Skip this pattern, will fall through to HttpErrorResponse handling
          }
        }
        console.log('Pattern matched:', pattern.name, '-> Message:', pattern.code);
        errorMessage = pattern.code;
        errorCategory = pattern.category;
        break;
      }
    }
    if (error?.name === 'HttpErrorResponse') {
      if (errorString.includes('serviceprovideravailability')) {
          errorMessage = GLOBAL_ERROR_CODES.ERROR_SP_AVAILABILITY;
        errorCategory = ERROR_CATEGORIES.SERVER;
      } else if (errorString.includes('504')) {
          errorMessage = GLOBAL_ERROR_CODES.ERROR_TIME_OUT;
        errorCategory = ERROR_CATEGORIES.TIMEOUT;
      } else {
          errorMessage = GLOBAL_ERROR_CODES.ERROR_API;
        errorCategory = ERROR_CATEGORIES.SERVER;
      }
    }

    const errorCode = this.generateErrorCode(errorCategory, moduleCode);
    const finalMessage = `${errorMessage} [Error Code: ${errorCode}]`;
    
    console.log('Final error message:', finalMessage);
    console.log('Error code:', errorCode);
    
    return {
      message: finalMessage,
      errorCode
    };
  }

  private detectModule(error: any, errorString: string): MODULE_CODES {
    const url = window.location.href.toLowerCase();
    const stack = (error?.stack || '').toLowerCase();
    const errorLower = errorString.toLowerCase();
    
    const searchText = `${url} ${stack} ${errorLower}`;
    
    const modulePatterns = [
      { keywords: ['login', 'auth', 'authentication', 'token', 'msal', 'azure', 'b2c', 'session', 'aadb2c', 'interaction_in_progress', 'invalid_token', 'token_expired', 'login_required'], module: MODULE_CODES.LOGIN },
      { keywords: ['service', 'servicer', 'service-order', 'serviceorder'], module: MODULE_CODES.SERVICES },
      { keywords: ['contract', 'agreement'], module: MODULE_CODES.CONTRACTS },
      { keywords: ['dealer', 'location'], module: MODULE_CODES.DEALERS },
      { keywords: ['claim', 'payment'], module: MODULE_CODES.CLAIMS },
      { keywords: ['import', 'upload', 'import-file'], module: MODULE_CODES.IMPORT_FILE },
      { keywords: ['buying-group', 'buyinggroup'], module: MODULE_CODES.BUYING_GROUP },
      { keywords: ['catalog', 'category', 'manufacturer'], module: MODULE_CODES.CATALOG },
      { keywords: ['coverage', 'rate', 'pricing'], module: MODULE_CODES.COVERAGE },
      { keywords: ['admin', 'administration', 'security', 'configuration'], module: MODULE_CODES.ADMINISTRATION },
      { keywords: ['report', 'extract', 'export'], module: MODULE_CODES.REPORTS }
    ];
    for (const { keywords, module } of modulePatterns) {
      if (keywords.some(keyword => searchText.includes(keyword))) {
        console.log(`Module detected: ${module} (matched: ${keywords.find(k => searchText.includes(k))})`);
        return module;
      }
    }

    const activeTabModule = this.getActiveTabModule();
    if (activeTabModule !== MODULE_CODES.UNKNOWN) {
      console.log(`Module detected from active tab: ${activeTabModule}`);
      return activeTabModule;
    }

    console.log('Module detection failed, returning UNKNOWN');
    return MODULE_CODES.UNKNOWN;
  }

  private getActiveTabModule(): MODULE_CODES {
    try {
      const activeTab = document.querySelector('.p-tabview-selected, .active-tab, [aria-selected="true"]');
      if (!activeTab?.textContent) return MODULE_CODES.UNKNOWN;
      
      const tabText = activeTab.textContent.toLowerCase();
      const tabPatterns = [
        { keywords: ['service'], module: MODULE_CODES.SERVICES },
        { keywords: ['contract'], module: MODULE_CODES.CONTRACTS },
        { keywords: ['dealer'], module: MODULE_CODES.DEALERS },
        { keywords: ['claim'], module: MODULE_CODES.CLAIMS },
        { keywords: ['import'], module: MODULE_CODES.IMPORT_FILE }
      ];

      for (const { keywords, module } of tabPatterns) {
        if (keywords.some(keyword => tabText.includes(keyword))) {
          return module;
        }
      }
    } catch (tabError) {
      console.warn('Error detecting active tab module:', tabError);
    }
    return MODULE_CODES.UNKNOWN;
  }

  private generateErrorCode(category: ERROR_CATEGORIES, module: MODULE_CODES): string {
    const categoryKey = `${category}-${module}`;
    
    if (!this.errorSequenceCounters.has(categoryKey)) {
      this.errorSequenceCounters.set(categoryKey, 0);
    }
    
    const counter = this.errorSequenceCounters.get(categoryKey)! + 1;
    this.errorSequenceCounters.set(categoryKey, counter);
    
    return `${category}-${module}-${counter.toString().padStart(2, '0')}`;
  }

  private extractErrorString(errorObj: any): string {
    let fullErrorString = errorObj.toString();
    
    const messageProps = ['message', 'error', 'statusText', 'details', 'description', 'name'];
    
    for (const prop of messageProps) {
      if (errorObj[prop]) {
        const propValue = String(errorObj[prop]);
        fullErrorString += ' ' + propValue;
      }
    }

    if (errorObj.error && typeof errorObj.error === 'object') {
      fullErrorString += ' ' + this.extractErrorString(errorObj.error);
    }

    if (errorObj.stack) {
      fullErrorString += ' ' + errorObj.stack;
    }

    return fullErrorString;
  }

  private getHttpStatusMessage(error: any): { message: string; category: ERROR_CATEGORIES } | null {
    // Check for status in various locations (standard HTTP errors, Apollo errors, etc.)
    let status = error?.status || error?.statusCode;
    
    // For Apollo errors, check networkError
    if (!status && error?.networkError) {
      status = error.networkError?.status || error.networkError?.statusCode;
    }
    
    // If still no status, try to extract from error message (e.g., "500 OK" or ": 500")
    const messageToCheck = error?.message || error?.networkError?.message || error?.toString?.() || '';
    if (!status && messageToCheck) {
      const statusMatch = messageToCheck.match(/: (\d{3})/);
      if (statusMatch) {
        status = parseInt(statusMatch[1], 10);
      }
    }
    
    if (!status) return null;

    switch (status) {
      case 0:
        return { message: GLOBAL_ERROR_CODES.ERROR_NETWORK, category: ERROR_CATEGORIES.NETWORK };
      case 400:
        return { message: GLOBAL_ERROR_CODES.ERROR_VALIDATION, category: ERROR_CATEGORIES.VALIDATION };
      case 401:
        return { message: GLOBAL_ERROR_CODES.ERROR_UNAUTHORIZED, category: ERROR_CATEGORIES.AUTHENTICATION };
      case 403:
        return { message: GLOBAL_ERROR_CODES.ERROR_FORBIDDEN, category: ERROR_CATEGORIES.AUTHORIZATION };
      case 404:
        return { message: GLOBAL_ERROR_CODES.ERROR_NOT_FOUND, category: ERROR_CATEGORIES.CLIENT };
      case 408:
        return { message: GLOBAL_ERROR_CODES.ERROR_TIME_OUT, category: ERROR_CATEGORIES.TIMEOUT };
      case 409:
        return { message: GLOBAL_ERROR_CODES.ERROR_CONFLICT, category: ERROR_CATEGORIES.VALIDATION };
      case 422:
        return { message: GLOBAL_ERROR_CODES.ERROR_VALIDATION, category: ERROR_CATEGORIES.VALIDATION };
      case 429:
        return { message: GLOBAL_ERROR_CODES.ERROR_TOO_MANY_REQUESTS, category: ERROR_CATEGORIES.CLIENT };
      case 500:
        return { message: 'A server error occurred. Our team has been notified. Please try again later.', category: ERROR_CATEGORIES.SERVER };
      case 502:
        return { message: 'The service is temporarily unavailable. Please try again in a few moments.', category: ERROR_CATEGORIES.SERVER };
      case 503:
        return { message: 'The service is currently under maintenance. Please try again later.', category: ERROR_CATEGORIES.SERVER };
      case 504:
        return { message: GLOBAL_ERROR_CODES.ERROR_TIME_OUT, category: ERROR_CATEGORIES.TIMEOUT };
      default:
        if (status >= 500) {
          return { message: GLOBAL_ERROR_CODES.ERROR_API, category: ERROR_CATEGORIES.SERVER };
        }
        return null;
    }
  }

  private logErrorToBackend(originalError: any, errorResult: { message: string; errorCode: string }): void {
    try {
      const FrontendErrorInput = {
        errorMessage: errorResult.message,
        errorStack: originalError?.stack || '',
        errorType: originalError?.name || typeof originalError,
        column: this.extractColumnNumber(originalError),
        state: JSON.stringify({ timestamp: new Date().toISOString() }),
      };

      const logErrorMutation = gql`
        mutation ($input: FrontendErrorInput!) {
          logError(error: $input) {
            success
            message
          }
        }
      `;

      this.apollo.use(CrmGraphql.NameSpace).mutate({
        mutation: logErrorMutation,
        variables: { input: FrontendErrorInput }
      }).subscribe({
        next: () => console.log('Error logged to backend'),
        error: (err) => console.warn('Failed to log error to backend:', err)
      });
    } catch (logError) {
      console.warn('Error while logging to backend:', logError);
    }
  }









  private extractColumnNumber(error: any): number {
    if (error?.colno) return error.colno;
    if (error?.stack) {
      const match = error.stack.match(/:(\d+):(\d+)/);
      return match ? parseInt(match[2]) : 0;
    }
    return 0;
  }





  private handleAuthenticationErrors(error: any, errorResult: { message: string; errorCode: string }): boolean {
    const errorString = this.extractErrorString(error).toLowerCase();
    
    const isAuthError = errorResult.errorCode.includes('LG-') || 
                       errorString.includes('token') || 
                       errorString.includes('auth') || 
                       errorString.includes('login') ||
                       errorString.includes('session') ||
                       errorString.includes('aadb2c');

    if (isAuthError) {
      console.log('Authentication error detected:', errorResult.errorCode);
      
      // Check for token expiration and redirect to login
      if (errorString.includes('token renewal operation failed') || 
          errorString.includes('token_expired') ||
          errorString.includes('invalid_token') ||
          errorString.includes('aadb2c90080') ||
          errorString.includes('grant has expired')) {
        setTimeout(() => {
          if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
            console.log('Redirecting to login due to token expiration');
            window.location.href = '/';
          }
        }, 3000);
        return true; // Don't show error, redirecting
      }
      
      if (errorString.includes('aadb2c90118') || 
          errorString.includes('user has forgotten their password')) {
        console.log('Redirecting to login for password reset');
        setTimeout(() => {
          try {
            sessionStorage.setItem(CRM_B2C_FORGOT_PASSWORD_RECOVERY_SESSION_KEY, '1');
          } catch {
            /* ignore */
          }
          window.location.href = '/?error=AADB2C90118';
        }, 100);
        return true; // Suppress error display
      }
      
      if (errorString.includes('aadb2c90205')) {
        console.log('MFA required');
      }
      
      // Handle interaction_in_progress - redirect to login silently (don't show error)
      if (errorString.includes('interaction_in_progress')) {
        console.log('Authentication interaction already in progress - redirecting silently');
        setTimeout(() => {
          if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
            window.location.href = '/';
          }
        }, 100);
        return true; // Don't show error, redirecting
      }
      
      // Handle InteractionRequiredAuthError - redirect to login silently
      if (errorString.includes('interactionrequiredautherror') || 
          errorString.includes('aadb2c90077') ||
          errorString.includes('user does not have an existing session')) {
        setTimeout(() => {
          if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
            console.log('Redirecting to login due to authentication required');
            window.location.href = '/';
          }
        }, 100);
        return true; // Don't show error, redirecting
      }
      
      // Handle BrowserAuthError: no_account_error - redirect to login silently
      if (errorString.includes('no_account_error') || 
          errorString.includes('no account object provided') ||
          errorString.includes('no active account has been set')) {
        setTimeout(() => {
          if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
            console.log('Redirecting to login due to no account');
            window.location.href = '/';
          }
        }, 100);
        return true; // Don't show error, redirecting
      }
    }
    
    return false; // Show error normally
  }
}
